import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from "react";

/* -------------------------------------------------------------------------- */
/* Button                                                                      */
/* -------------------------------------------------------------------------- */
export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: ButtonVariant;
}

export function Button({
  variant = "secondary",
  className = "",
  type = "button",
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      className={`btn btn-${variant} ${className}`.trim()}
      {...rest}
    />
  );
}

/* -------------------------------------------------------------------------- */
/* Card                                                                        */
/* -------------------------------------------------------------------------- */
export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  readonly padded?: boolean;
}

export function Card({ padded = true, className = "", ...rest }: CardProps) {
  return (
    <div
      className={`${padded ? "card" : "surface"} ${className}`.trim()}
      {...rest}
    />
  );
}

/**
 * The heading level a card's title occupies in the document outline.
 *
 * A card is usually a top-level section under the page's `h1`, so `h2` is the
 * default. Pass `h3` when the card sits inside a section that already has its
 * own `h2`, so the outline never skips a level.
 */
export type CardHeadingLevel = "h2" | "h3" | "h4";

export function CardHeader({
  title,
  description,
  action,
  as: Heading = "h2",
  id,
}: {
  readonly title: ReactNode;
  readonly description?: ReactNode;
  readonly action?: ReactNode;
  readonly as?: CardHeadingLevel;
  readonly id?: string;
}) {
  return (
    <div className="mb-4 flex items-start justify-between gap-3">
      <div className="min-w-0">
        <Heading
          id={id}
          className="text-sm font-semibold"
          style={{ color: "var(--fg)" }}
        >
          {title}
        </Heading>
        {description ? (
          <p className="mt-0.5 text-xs" style={{ color: "var(--fg-muted)" }}>
            {description}
          </p>
        ) : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Badge + status dot                                                          */
/* -------------------------------------------------------------------------- */
export type BadgeTone = "neutral" | "success" | "warning" | "danger" | "accent";

export function Badge({
  tone = "neutral",
  children,
}: {
  readonly tone?: BadgeTone;
  readonly children: ReactNode;
}) {
  return <span className={`badge badge-${tone}`}>{children}</span>;
}

export function StatusDot({ tone = "neutral" }: { readonly tone?: BadgeTone }) {
  const color =
    tone === "success"
      ? "var(--success)"
      : tone === "warning"
        ? "var(--warning)"
        : tone === "danger"
          ? "var(--danger)"
          : tone === "accent"
            ? "var(--accent)"
            : "var(--fg-subtle)";
  return (
    <span
      aria-hidden
      className="inline-block h-2 w-2 shrink-0 rounded-full"
      style={{ backgroundColor: color }}
    />
  );
}

/* -------------------------------------------------------------------------- */
/* Stat tile                                                                   */
/* -------------------------------------------------------------------------- */
export function Stat({
  label,
  value,
  hint,
  tone,
}: {
  readonly label: ReactNode;
  readonly value: ReactNode;
  readonly hint?: ReactNode;
  readonly tone?: BadgeTone;
}) {
  return (
    <div className="card">
      <p
        className="text-xs font-medium uppercase tracking-wide"
        style={{ color: "var(--fg-subtle)" }}
      >
        {label}
      </p>
      <p
        className="mt-1 text-2xl font-semibold tabular-nums"
        style={{ color: "var(--fg)" }}
      >
        {value}
      </p>
      {hint ? (
        <p
          className="mt-1 text-xs"
          style={{
            color: tone === "danger" ? "var(--danger)" : "var(--fg-muted)",
          }}
        >
          {hint}
        </p>
      ) : null}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Skeleton                                                                    */
/* -------------------------------------------------------------------------- */
export function Skeleton({ className = "" }: { readonly className?: string }) {
  return (
    <div
      className={`animate-pulse rounded-md ${className}`.trim()}
      style={{ backgroundColor: "var(--bg-muted)" }}
      aria-hidden
    />
  );
}

/* -------------------------------------------------------------------------- */
/* Page header                                                                 */
/* -------------------------------------------------------------------------- */
export function PageHeader({
  title,
  description,
  actions,
}: {
  readonly title: ReactNode;
  readonly description?: ReactNode;
  readonly actions?: ReactNode;
}) {
  return (
    <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div className="min-w-0">
        <h1
          className="text-2xl font-bold tracking-tight"
          style={{ color: "var(--fg)" }}
        >
          {title}
        </h1>
        {description ? (
          <p className="mt-1 text-sm" style={{ color: "var(--fg-muted)" }}>
            {description}
          </p>
        ) : null}
      </div>
      {actions ? (
        <div className="flex shrink-0 items-center gap-2">{actions}</div>
      ) : null}
    </div>
  );
}
